import { ArrowDown, Egg, Clock, Award } from 'lucide-react';
import EggTimer from '../components/egg-timer/EggTimer';
import EggInstructions from '../components/egg-timer/EggInstructions';
import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div className="flex flex-col items-center">
      {/* Hero Section */}
      <section className="w-full bg-gradient-to-b from-primary-100 to-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Egg Timer Online - Perfect Boiled Eggs Every Time
          </h1>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            The most convenient online egg boiling timer to make perfect eggs exactly to your preference.
            Customize your settings and enjoy perfectly cooked eggs every time.
          </p>
          <div className="flex justify-center gap-4 mb-8">
            <Link
              to="#egg-timer"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-full shadow-md hover:bg-primary-600 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('egg-timer')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <Clock className="w-5 h-5 mr-2" />
              Start Egg Timer
            </Link>
            <a
              href="/recipes"
              target="_blank"
              className="inline-flex items-center px-6 py-3 bg-white text-primary-600 border border-primary-300 rounded-full shadow-sm hover:bg-primary-50 transition-colors"
            >
              <Egg className="w-5 h-5 mr-2" />
              Egg Recipes
            </a>
          </div>
          <div className="flex justify-center animate-bounce">
            <ArrowDown className="w-6 h-6 text-primary-500" />
          </div>
        </div>
      </section>

      {/* Timer Section */}
      <section id="egg-timer" className="w-full py-12 scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <span className="inline-block bg-primary-100 text-primary-800 px-4 py-1 rounded-full text-sm font-medium mb-3">
              Easy to Use
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              The Ultimate Egg Boiling Timer
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select your preferred egg readiness, customize the settings, and let our timer guide you to perfectly boiled eggs.
            </p>
          </div>
          
          <EggTimer />
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-gray-800 mb-4">
              Why Use Our Egg Timer Online?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our egg boiling timer is designed to give you perfect results every time with customizable settings and helpful guidance.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">Precision Timing</h3>
              <p className="text-gray-600">
                Our egg timer calculates the precise cooking time based on your egg preferences and settings.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-primary-600">
                  <path d="M12 22a8 8 0 0 0 8-8" />
                  <path d="M20 10c.9-1.1 1.5-2.4 1.8-3.8.3-1.4.2-2.8-.2-4.1-.4-1.3-1.2-2.4-2.2-3.3C18.4-2 16.4-2.5 14.5-2c-1.9.5-3.5 1.8-4.6 3.5C8.9 3.2 8.5 5.3 9 7.3" />
                  <path d="M12 22a8 8 0 0 1-8-8" />
                  <path d="M4 10c-.9-1.1-1.5-2.4-1.8-3.8-.3-1.4-.2-2.8.2-4.1C2.8.8 3.6-.3 4.6-1.2 5.6-2 7.6-2.5 9.5-2c1.9.5 3.5 1.8 4.6 3.5 1 1.7 1.4 3.8.9 5.8" />
                  <path d="M12 22V10" />
                  <path d="M12 10 8 7" />
                  <path d="m12 10 4-3" />
                </svg>
              </div>
              <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">Customizable Settings</h3>
              <p className="text-gray-600">
                Adjust for egg temperature, size, and cooking method to get your eggs exactly how you like them.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-primary-600" />
              </div>
              <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">Expert Tips</h3>
              <p className="text-gray-600">
                Learn professional techniques and tips for cooking, peeling, and enjoying perfectly boiled eggs.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Instructions Section */}
      <section id="instructions" className="w-full py-12 scroll-mt-20">
        <EggInstructions />
      </section>
      
      {/* Call to Action */}
      <section className="w-full bg-primary-50 py-12">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-display text-3xl font-bold text-gray-800 mb-4">
            Ready to Make Perfect Eggs?
          </h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Try our online egg timer now for the perfect soft-boiled, medium, or hard-boiled eggs. Customize your settings and enjoy!
          </p>
          <Link
            to="#egg-timer"
            className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-full shadow-md hover:bg-primary-600 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('egg-timer')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Clock className="w-5 h-5 mr-2" />
            Start Egg Timer
          </Link>
          
          <div className="mt-12 flex flex-col md:flex-row justify-center items-center gap-6 text-left">
            <div className="bg-white p-6 rounded-xl shadow-md max-w-md">
              <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">
                Explore Egg Recipes
              </h3>
              <p className="text-gray-600 mb-4">
                Looking for delicious ways to enjoy your perfectly boiled eggs? Check out our collection of egg recipes.
              </p>
              <a
                href="/recipes"
                target="_blank"
                className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                Browse Recipes →
              </a>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md max-w-md">
              <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">
                Have Questions?
              </h3>
              <p className="text-gray-600 mb-4">
                Need help with the egg timer or have suggestions for improvement? Contact our team.
              </p>
              <a
                href="/contact"
                target="_blank"
                className="text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                Contact Us →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;